/*
Copyright (C) 2009 - 2019 Broadleaf Commerce.

Licensed under the Broadleaf End User License Agreement (EULA),
Version 1.1 (the “Commercial License” located at
http://license.broadleafcommerce.org/commercial_license-1.1.txt).

Alternatively, the Commercial License may be replaced with a mutually
agreed upon license (the “Custom License”) between you and
Broadleaf Commerce. You may not use this file except in compliance
with the applicable license.
*/
import React, { useMemo } from 'react';
import { get, pick } from 'lodash';

import { ToggleSwitch } from '@broadleaf/admin-components/dist/form/components/ToggleSwitch';
import useFormatMessage from '@broadleaf/admin-components/dist/common/hooks/useFormatMessage';

import messages from './BooleanRowToggleField.messages';
import type { IMetadataEndpoint } from '@broadleaf/admin-components/dist/types/metadata';
import useTranslateMode from '@broadleaf/admin-components/dist/form/hooks/useTranslateMode';
import { Formik } from 'formik';
import { request } from '@broadleaf/admin-components/dist/metadata/utils/request';
import {
  ContextParameters,
  getInheritedContextState
} from '@broadleaf/admin-components/dist/tracking/utils/ContextRequest';
import { FieldError } from '@broadleaf/admin-components/dist/form';
import { hooks } from '@broadleaf/admin-components';
import { publishRowEdit } from 'app/common/utils/rowEdits';
const { useEventCallback } = hooks;

const OPTION_YES = {
  color: 'primary',
  value: true
};
const OPTION_NO = {
  color: 'danger',
  value: false
};

/**
 * Renders a ToggleSwitch for a BOOLEAN field type.
 *
 * @visibleName BOOLEAN
 */
const BooleanRowToggleField: React.FC<BooleanRowToggleFieldProps> = props => {
  const {
    disabled,
    name,
    updateEndpoint,
    updateFields,
    readOnly,
    row,
    contextParams,
    fieldMappings,
    rowId
  } = props;
  const fieldNames = [...updateFields, name];
  const allFields = { ...row, ...contextParams };
  const initialValues = useMemo(
    () => pick(allFields, fieldNames),
    [allFields, fieldNames]
  );
  const value = get(initialValues, name, false);
  const [toggleValue, setToggleValue] = React.useState(value);
  // keep in sync when the grid re-fetches its rows
  React.useEffect(() => setToggleValue(value), [value]);
  const formatMessage = useFormatMessage();
  const [error, setError] = React.useState(undefined);
  const options = useMemo(() => {
    return [
      {
        ...OPTION_YES,
        label: formatMessage(messages.trueLabel)
      },
      {
        ...OPTION_NO,
        label: formatMessage(messages.falseLabel)
      }
    ];
  }, [formatMessage]);
  const handleSubmit = useSubmitUpdate({
    updateEndpoint,
    contextParams,
    fieldMappings,
    fieldName: name,
    setError,
    toggleValue,
    setToggleValue,
    // let the row form action see the saved value
    onSaved: value => publishRowEdit(row, { [name]: value })
  });

  const translateMode = useTranslateMode();
  return (
    <>
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        enableReinitialize={true}
      >
        {formik => (
          <ToggleSwitch
            className="BooleanField"
            disabled={
              translateMode?.isActive ||
              disabled ||
              readOnly ||
              formik.isSubmitting
            }
            name={name + '-' + rowId}
            options={options}
            onChange={e => {
              setToggleValue(e);
              setError(undefined);
              formik.setFieldValue(name, e).then(() => {
                formik.submitForm();
              });
            }}
            value={toggleValue}
          />
        )}
      </Formik>
      {error ? <FieldError error={error} /> : null}
    </>
  );
};

function useSubmitUpdate({
  updateEndpoint,
  contextParams,
  fieldMappings,
  fieldName,
  setError,
  toggleValue,
  setToggleValue,
  onSaved
}) {
  return useEventCallback(
    async (values, formik) => {
      try {
        formik.setSubmitting(true);
        await request(
          { ...updateEndpoint, data: values },
          {
            ...contextParams,
            [ContextParameters.CONTEXT_STATE]: getInheritedContextState(values),
            ...values
          }
        );
        formik.setSubmitting(false);
        onSaved(get(values, fieldName));
      } catch (error) {
        formik.setFieldError(fieldName, error.message);
        formik.setFieldValue(!toggleValue);
        setToggleValue(!toggleValue);
        setError(error.message);
      }
    },
    [updateEndpoint, contextParams, fieldMappings]
  );
}

BooleanRowToggleField.defaultProps = {
  disabled: false,
  readOnly: false,
  name: undefined,
  updateEndpoint: undefined,
  updateFields: undefined,
  row: undefined,
  contextParams: undefined,
  fieldMappings: undefined,
  rowId: undefined
};

export interface BooleanRowToggleFieldProps {
  /** True if you want to disable the radio buttons, false otherwise. */
  disabled?: boolean;
  readOnly: false;
  updateEndpoint: IMetadataEndpoint;
  /** The name of the field that determines boolean state. */
  name: string;
  /** The fields that should be submitted when the toggle is changed. */
  updateFields: string[];
  row: any;
  contextParams?: any;
  fieldMappings?: any;
  rowId: string;
}

export { BooleanRowToggleField };
